import { useCallback, useRef, useContext, useEffect, useMemo } from 'react';
import useField from 'hooks/useField';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast, toastVariant } from 'components/ToastProvider';
import styled from '@emotion/styled';
import {
  NotesRichTextEditor,
  parseMentions,
} from 'components/Inputs/NotesRichTextEditor';
import { defaultFonts } from 'components/WYSIWYG';
import { useTranslation } from 'react-i18next';
import LoadingButton from 'components/Button/LoadingButton';
import ClientService from 'services/ClientService';
import ActivityService from 'services/ActivityService';
import { gutters } from 'app/spacing';
import { shadowLight } from 'app/colors';
import { fontSizes, KizenTypography } from 'app/typography';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import { TextEllipsis, TextEllipsisWithTooltip } from 'components/Kizen/Table';
import { useModalControl } from 'hooks/useModalControl';
import { NotesContext } from './notesContext';
import { ConfirmSendNotification } from './ScheduleActivityModal';
import { CLIENTS } from 'queries/query-keys';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { ADORNMENT_VARIANTS } from 'components/Inputs/Adornments/constants';
import { WithIconDataAdornments } from 'components/Inputs/Adornments/WithIconDataAdornments';
import css from '@emotion/css';

const NBSP = '\xa0';
const NO_VALUE = '—';

const WRAPPER_HEIGHT = 197;

const Wrapper = styled.div`
  white-space: normal;
  display: flex;
  ${({ mobileView }) =>
    mobileView
      ? 'flex-direction: column;'
      : `
        margin: ${gutters.spacing(2, -2)}px 0 0 0;
        height: ${WRAPPER_HEIGHT}px;
        `}
  ${({ width }) => (width ? `width: ${width - 40}px` : '')}
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ minSize }) => (minSize ? `margin-right: 40px;` : ``)}
  width: ${({ width }) => (width ? `${width}px` : '100%')};
`;

const TableActivityInfo = styled.table`
  width: 100%;

  tr {
    &:last-child {
      td {
        padding-bottom: 0;
      }
    }
  }

  td > a,
  td > p {
    max-width: 200px;
  }
  td {
    padding-bottom: 6px;

    &:first-child {
      min-width: calc(98px + 10px); /* width + padding */
      width: calc(98px + 10px); /* width + padding */
    }
  }
`;

const EditorWrapper = styled.div`
  && {
    flex: 1;
    ${shadowLight}
    border-radius: 4px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border: none;
    resize: none;
    outline: none;
    ${({ mobileView }) =>
      mobileView ? `margin-top: 17px; ` : `margin-top: 7px; `}
    width: ${({ width }) => (width ? `${width}px` : '100%')};
    font-size: ${fontSizes.text};
    line-height: 115%;
    z-index: 1;
  }
`;

const SaveWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: right;
  height: 30px;
  padding: 10px;
`;

const SaveButton = styled(LoadingButton)`
  height: unset;
  min-width: 32px;
`;
const Card = styled.div`
  padding: ${gutters.standard};
  ${shadowLight}
  ${({ mobileView }) =>
    mobileView
      ? `margin: 10px 0;`
      : `
      margin-left: 40px;
      margin-right: 40px;
      padding-bottom: 0;`}
  background-color: var(--color-background-white);
  border-radius: 4px;
  z-index: 1;
  width: 100%;
  display: flex;
  align-content: flex-start;
  flex-wrap: wrap;
`;

const CardItemWrapper = styled.div`
  padding-bottom: calc(20px - 4px);
  &:nth-child(2n) {
    width: calc(100% - (125px + 50px));
    padding-left: 20px;
  }
  &:nth-child(2n + 1) {
    width: calc(125px + 50px);
    padding-right: 20px;
  }
  p + * {
    /**10px between label and text. Also generalizes a bootstrap thing for <p> to anchors, etc. */
    margin-top: 6px;
    ${({ hasAdornments }) => {
      return hasAdornments
        ? ''
        : css`
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `;
    }};
  }
`;

function CardItem({ label, children, hasAdornments, ...others }) {
  return (
    <CardItemWrapper {...others} hasAdornments={hasAdornments}>
      <KizenTypography className="card-item-label">{label}</KizenTypography>
      {children}
    </CardItemWrapper>
  );
}

function ClientCard({
  clientId = null,
  contactAccess,
  mobileView = false,
  hide,
  adornments,
}) {
  const { t } = useTranslation();

  // tooltips
  const [mobilePhoneProps, mobilePhoneTooltip] = useTruncationTooltip();
  const [businessPhoneProps, businessPhoneTooltip] = useTruncationTooltip();
  const [emailProps, emailTooltip] = useTruncationTooltip();

  const [clientQuery, addressQuery] = useQueries({
    queries: [
      {
        queryKey: CLIENTS.ENTITY(clientId),
        queryFn: async () => {
          const data = await ClientService.getById(clientId);
          return data;
        },
        enabled: !!clientId && contactAccess,
      },
      {
        queryKey: CLIENTS.ADDRESSES(clientId),
        queryFn: async () => ClientService.getAddressesById(clientId),
        enabled: !!clientId && contactAccess,
      },
    ],
  });

  const { client, loading, error } = useMemo(() => {
    return {
      client: {
        ...clientQuery.data,
        addresses: addressQuery.data,
      },
      loading: clientQuery.isLoading || addressQuery.isLoading,
      error: clientQuery.error || addressQuery.error,
    };
  }, [clientQuery, addressQuery]);

  const [showToast] = useToast();

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId: client?.id,
    objectId: client?.objectId,
    onError: (e) => {
      showToast({
        message: `${t('Action could not run')}: ${e?.message}`,
        variant: toastVariant.FAILURE,
      });
    },
  });

  if (error || !contactAccess || !clientId || hide) {
    return null;
  }

  if (loading) {
    return (
      <Card mobileView={mobileView}>
        <CardItem label={t('Title')}>
          <KizenTypography>{NBSP}</KizenTypography>
        </CardItem>
        <CardItem label={t('Mobile Phone')}>
          <KizenTypography>{NBSP}</KizenTypography>
        </CardItem>
        <CardItem label={t('Email')}>
          <KizenTypography>{NBSP}</KizenTypography>
        </CardItem>
        <CardItem label={t('Business Phone')}>
          <KizenTypography>{NBSP}</KizenTypography>
        </CardItem>
        <CardItem />
      </Card>
    );
  }

  const phoneAdornments = adornments.filter(
    (adornment) => adornment.field_type === 'phonenumber'
  );

  const titles = client && client.titles;
  const mobilePhone = client && client.mobilePhone;
  const email = client && client.email;
  const businessPhone = client && client.businessPhone;
  const hasAdornments = phoneAdornments.length > 0;

  return mobileView ? (
    <Card mobileView={mobileView}>
      <TableActivityInfo>
        <tr>
          <td>
            <KizenTypography type="small" size="text">
              {t('Title')}
            </KizenTypography>
          </td>
          <td>
            <TextEllipsis type="small" size="text">
              {titles?.map(({ name }) => name)?.join(', ') || NO_VALUE}
            </TextEllipsis>
          </td>
        </tr>
        <tr>
          <td>
            <KizenTypography type="small" size="text">
              {t('Mobile Phone')}
            </KizenTypography>
          </td>
          <td>
            {mobilePhone ? (
              <>
                {mobilePhoneTooltip}
                <TextEllipsis
                  type="anchor"
                  size="text"
                  href={`tel:${mobilePhone}`}
                  {...mobilePhoneProps}
                >
                  {mobilePhone}
                </TextEllipsis>
              </>
            ) : (
              <KizenTypography type="small" size="text">
                {NO_VALUE}
              </KizenTypography>
            )}
          </td>
        </tr>
        <tr>
          <td>
            <KizenTypography type="small" size="text">
              {t('Business Phone')}
            </KizenTypography>
          </td>
          <td>
            {businessPhone ? (
              <>
                {businessPhoneTooltip}
                <TextEllipsis
                  type="anchor"
                  size="text"
                  href={`tel:${businessPhone}`}
                  {...businessPhoneProps}
                >
                  {businessPhone}
                </TextEllipsis>
              </>
            ) : (
              <KizenTypography type="small" size="text">
                {NO_VALUE}
              </KizenTypography>
            )}
          </td>
        </tr>
        <tr>
          <td>
            <KizenTypography type="small" size="text">
              {t('Email')}
            </KizenTypography>
          </td>
          <td>
            {email ? (
              <>
                {emailTooltip}
                <TextEllipsis
                  type="anchor"
                  size="text"
                  href={`mailto:${email}`}
                  {...emailProps}
                >
                  {email}
                </TextEllipsis>
              </>
            ) : (
              <KizenTypography type="small" size="text">
                {NO_VALUE}
              </KizenTypography>
            )}
          </td>
        </tr>
      </TableActivityInfo>
    </Card>
  ) : (
    <Card mobileView={mobileView}>
      <CardItem label={t('Title')}>
        {(!titles || !titles.length) && (
          <KizenTypography>{NO_VALUE}</KizenTypography>
        )}
        {titles && !!titles.length && (
          <TextEllipsisWithTooltip weight="bold">
            {titles.map(({ name }) => name).join(', ')}
          </TextEllipsisWithTooltip>
        )}
      </CardItem>
      <CardItem
        label={t('Mobile Phone')}
        hasAdornments={hasAdornments && mobilePhone}
      >
        {!mobilePhone && <KizenTypography>{NO_VALUE}</KizenTypography>}
        {mobilePhone && (
          <WithIconDataAdornments
            hasAdornments={hasAdornments}
            adornments={phoneAdornments}
            scriptArgs={{
              objectId: client?.id,
              fieldId: 'mobile_phone',
              fieldType: 'phone_number',
              value: mobilePhone,
            }}
            disabled={internalState.pending}
            executeScript={executeScript}
            variant={ADORNMENT_VARIANTS.client_card}
          >
            {mobilePhoneTooltip}
            <TextEllipsis
              type="anchor"
              size="text"
              href={`tel:${mobilePhone}`}
              {...mobilePhoneProps}
            >
              {mobilePhone}
            </TextEllipsis>
          </WithIconDataAdornments>
        )}
      </CardItem>
      <CardItem label={t('Email')}>
        {!email && <KizenTypography>{NO_VALUE}</KizenTypography>}
        {email && (
          <KizenTypography type="anchor" href={`mailto:${email}`}>
            {email}
          </KizenTypography>
        )}
      </CardItem>
      <CardItem
        label={t('Business Phone')}
        hasAdornments={hasAdornments && businessPhone}
      >
        {!businessPhone && <KizenTypography>{NO_VALUE}</KizenTypography>}
        {businessPhone && (
          <WithIconDataAdornments
            hasAdornments={hasAdornments}
            adornments={phoneAdornments}
            scriptArgs={{
              objectId: client?.id,
              fieldId: 'business_phone',
              fieldType: 'phone_number',
              value: businessPhone,
            }}
            disabled={internalState.pending}
            executeScript={executeScript}
            variant={ADORNMENT_VARIANTS.client_card}
          >
            {businessPhoneTooltip}
            <TextEllipsis
              type="anchor"
              size="text"
              href={`tel:${businessPhone}`}
              {...businessPhoneProps}
            >
              {businessPhone}
            </TextEllipsis>
          </WithIconDataAdornments>
        )}
      </CardItem>
      <CardItem />
    </Card>
  );
}

export const BottomBarNode = ({
  loading,
  onClick,
  dataQA = 'save',
  ...other
}) => {
  const { t } = useTranslation();
  return (
    <SaveWrapper>
      <SaveButton
        loading={loading}
        color="blue"
        noSpace
        variant="text"
        onClick={onClick}
        loadingColor="var(--color-background-medium)"
        data-qa-save-button={dataQA}
        {...other}
      >
        {t('Save')}
      </SaveButton>
    </SaveWrapper>
  );
};

export default function NotesPanel({
  index,
  activity,
  contactAccess,
  onNotesChange,
  updateNoteLoading,
  mobileView = false,
  scheduledActivityQueryKey,
  activityPageById,
  adornments = [],
  inModal,
  setCurrentNote,
  containerWidth,
  onNoteSaved,
  ...others
}) {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const [showToast] = useToast();
  const [localNote, setLocalNote] = useField(activity.note, [activity.note]);
  const [modalOpen, { showModal, hideModal }] = useModalControl();
  const queryClient = useQueryClient();

  const {
    setIsDirty: contextSetIsDirty,
    setOriginalNote: contextSetOriginalNote,
    originalNote: contextOriginalNote,
  } = useContext(NotesContext);

  const isDirty = useMemo(
    () => contextOriginalNote !== localNote,
    [contextOriginalNote, localNote]
  );

  const phoneAdornments = adornments.filter(
    (adornment) => adornment.field_type === 'phonenumber'
  );

  const { mutate: saveNotes, isPending: notesSaving } = useMutation({
    mutationFn: async (notifyMentioned = false) =>
      await ActivityService.updateScheduledActivityNotes(activity.id, {
        note: localNote,
        mentions: parseMentions(localNote),
        notify_mentioned: notifyMentioned,
      }),
    onSuccess: () => {
      contextSetOriginalNote(localNote);
      contextSetIsDirty(false);

      onNotesChange(false);
    },
    onError: ({ payload }) => {
      showToast({
        message:
          payload?.response?.data?.message ||
          t(
            'The Activity Notes were not saved. Please try again or contact Kizen support.'
          ),
        variant: toastVariant.FAILURE,
      });
    },
    onSettled: async ({ note, mentions }) => {
      await queryClient.cancelQueries({ queryKey: scheduledActivityQueryKey });
      const results = queryClient.getQueryData(scheduledActivityQueryKey);

      const { pages: previousPages, ...rest } = results;

      const updatedPages = structuredClone(previousPages);
      if (activityPageById) {
        const { pageIndex, resultsIndex } = activityPageById[activity.id];
        if (updatedPages[pageIndex] && updatedPages[pageIndex].results) {
          updatedPages[pageIndex].results[resultsIndex] = {
            ...updatedPages[pageIndex].results[resultsIndex],
            note,
            mentions,
          };
        }
      }
      queryClient.setQueryData(scheduledActivityQueryKey, {
        ...rest,
        pages: updatedPages,
      });
      onNoteSaved?.();
      return { ...rest, pages: updatedPages };
    },
  });

  const handleSaveNote = useCallback(async () => {
    if (parseMentions(localNote).length > 0) {
      showModal();
    } else {
      saveNotes();
    }
  }, [localNote, showModal, saveNotes]);

  const handleNoteChange = useCallback(() => {
    const note = editorRef.current.isEmpty()
      ? ''
      : editorRef.current.editor.getHTML();
    setLocalNote(note);
    setCurrentNote(note);
    contextSetIsDirty(true);
  }, [setLocalNote, contextSetIsDirty, setCurrentNote]);

  useEffect(() => {
    editorRef.current.editor?.commands.setContent(activity.note);
  }, [activity.note]);

  const minSize = containerWidth < 700;
  const hideClientCard =
    (minSize && !mobileView) || (!activity?.client && !activity?.client?.id);
  const rightSideWidth = hideClientCard
    ? containerWidth - 80 // without client card we maintain 40 + 40 margin
    : containerWidth / 2 - 60; // with client card we have 40 + 20 margin

  const handleBlur = useCallback(() => {
    if (isDirty) {
      onNotesChange(index, localNote, isDirty);
    }
  }, [localNote, onNotesChange, index, isDirty]);

  return (
    <Wrapper mobileView={mobileView} {...others} width={containerWidth}>
      <Section minSize={minSize} width={rightSideWidth}>
        <KizenTypography weight="bold" margin="none">
          {`${t('Notes')}:`}
        </KizenTypography>
        <EditorWrapper mobileView={mobileView} width={rightSideWidth}>
          <NotesRichTextEditor
            appendToSelector="tbody"
            label={null}
            disabled={!activity.access.view}
            value={localNote}
            onChange={handleNoteChange}
            ref={editorRef}
            initialHeight={98}
            enableResize={false}
            defaultFonts={defaultFonts}
            onBlur={handleBlur}
            bottomBarNode={
              <BottomBarNode
                disabled={!activity.access.view || notesSaving}
                loading={notesSaving}
                onClick={handleSaveNote}
              />
            }
          />
        </EditorWrapper>
      </Section>
      <ClientCard
        as={Section}
        contactAccess={contactAccess}
        mobileView={mobileView}
        clientId={(activity.client && activity.client.id) || null}
        adornments={phoneAdornments}
        inModal={inModal}
        hide={hideClientCard}
      />
      {modalOpen && (
        <ConfirmSendNotification
          onCancel={hideModal}
          onDontSend={() => {
            hideModal();
            saveNotes(false);
          }}
          onSend={async () => {
            hideModal();
            saveNotes(true);
          }}
        />
      )}
    </Wrapper>
  );
}
